// Long-press detection hook for mobile receipts (P5 source-decomposed
// values surfaced via touch-and-hold). Fires `onLongPress` after the
// user holds for `holdMs` (default 400 ms — Apple's iOS context-menu
// threshold and a comfortable middle ground between accidental holds
// and arthritis-impacted hold latency).
//
// Cancellation rules: any movement past `moveTolerance` px, any
// onTouchEnd before the threshold, onTouchCancel, or scroll cancels
// the press. A short haptic fires the instant the long-press
// registers so the user knows the hold succeeded *before* the sheet
// animates in — closes the <100 ms HIG feedback budget.
//
// Returns spread-able handler props for a touchable element:
//   const lp = useLongPress(() => openReceipt());
//   <span {...lp.bind}>...</span>
//
// The hook is intentionally not coupled to a specific UI: callers
// open their own sheets, dispatch their own analytics, etc.
//
// Why 400 ms: AdjustButton's HOLD_DELAY_MS for adjuster ramp-up is
// 350 ms; 400 ms here keeps the two patterns distinguishable so a
// long-press on a stat doesn't conflict with a held adjuster button
// (different elements anyway, but the threshold separation is good
// hygiene).
//
// Known gaps (deferred to A.4 / A.5 hardening):
//
// - **Keyboard activation** is not currently supported. A user
//   navigating via Tab + Enter / Space hits the post-press `onClick`
//   with `triggeredRef.current === false` (no `start()` ever ran), so
//   the long-press handler is never invoked. For pilot use on the
//   HP bar (touch / mouse natural) this is acceptable; once long-
//   press wires onto Skills / Spells (A.2b) where keyboard users
//   matter more, pair the bind with an explicit `onKeyDown` for
//   Enter/Space holds, or pair the long-pressable element with a
//   keyboard-focusable "i" affordance that opens the same receipt.
//
// - **`bind.style` ordering**: the returned bind includes an inline
//   `style` (WebkitUserSelect: 'none', etc.) to prevent the OS text-
//   selection callout. If you spread `.bind` *after* your own style
//   prop, your styles win and selection prevention is lost; spread
//   `.bind` *before* and merge any caller styles after. Example:
//     <div style={{ ...lp.bind.style, ...yourStyles }} ...lp.bind />
//   The HP-bar pilot at App.js:63303 demonstrates the safe merge.

import { useCallback, useEffect, useRef } from 'react';
import { feedback } from './feedback';

const DEFAULT_HOLD_MS = 400;
const DEFAULT_MOVE_TOLERANCE_PX = 8;

export default function useLongPress(onLongPress, options = {}) {
  const {
    holdMs = DEFAULT_HOLD_MS,
    moveTolerance = DEFAULT_MOVE_TOLERANCE_PX,
    hapticOnTrigger = true,
    suppressClick = true,
  } = options;

  const timerRef = useRef(null);
  const startRef = useRef(null);
  const triggeredRef = useRef(false);
  const handlerRef = useRef(onLongPress);

  // Keep handler ref current without re-binding event listeners.
  useEffect(() => { handlerRef.current = onLongPress; }, [onLongPress]);

  // Cleanup on unmount.
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startRef.current = null;
  }, []);

  const start = useCallback((x, y) => {
    triggeredRef.current = false;
    startRef.current = { x, y };
    timerRef.current = setTimeout(() => {
      triggeredRef.current = true;
      if (hapticOnTrigger) feedback('tap');
      if (handlerRef.current) handlerRef.current();
    }, holdMs);
  }, [holdMs, hapticOnTrigger]);

  const checkMove = useCallback((x, y) => {
    if (!startRef.current) return;
    const dx = x - startRef.current.x;
    const dy = y - startRef.current.y;
    if (Math.sqrt(dx * dx + dy * dy) > moveTolerance) clear();
  }, [moveTolerance, clear]);

  const onTouchStart = useCallback((e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    start(t.clientX, t.clientY);
  }, [start]);

  const onTouchMove = useCallback((e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    checkMove(t.clientX, t.clientY);
  }, [checkMove]);

  const onTouchEnd = useCallback(() => { clear(); }, [clear]);
  const onTouchCancel = useCallback(() => { clear(); }, [clear]);

  const onMouseDown = useCallback((e) => { start(e.clientX, e.clientY); }, [start]);
  const onMouseMove = useCallback((e) => { checkMove(e.clientX, e.clientY); }, [checkMove]);
  const onMouseUp = useCallback(() => { clear(); }, [clear]);
  const onMouseLeave = useCallback(() => { clear(); }, [clear]);

  // Swallow the click that follows a successful long-press so the
  // element's onClick doesn't double-fire (e.g., opening detail view
  // AND the receipt sheet).
  const onClick = useCallback((e) => {
    if (suppressClick && triggeredRef.current) {
      e.preventDefault();
      e.stopPropagation();
      triggeredRef.current = false;
    }
  }, [suppressClick]);

  return {
    bind: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onTouchCancel,
      onMouseDown,
      onMouseMove,
      onMouseUp,
      onMouseLeave,
      onClick,
      // Inline style: prevent the OS text-selection callout on a long
      // touch-hold, which would otherwise compete with our handler.
      style: { WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none' },
    },
  };
}
